import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, Save, Users, Clock, DollarSign, 
  Music, MapPin, RefreshCw, Eye, Sparkles, GripVertical
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { adminFetch } from '@/utils/adminAuth';

const defaultPricingConfig = {
  // Personnel pricing (per person)
  personnel: [
    { id: '1', name: 'Lead Drummer', pricePerHour: 150, description: 'Master drummer directing the bateria' },
    { id: '2', name: 'Drummer', pricePerHour: 100, description: 'Surdo, caixa, tamborim players' },
    { id: '3', name: 'Lead Dancer', pricePerHour: 175, description: 'Featured dancer/choreographer' },
    { id: '4', name: 'Dancer', pricePerHour: 125, description: 'Samba dancers in costume' },
    { id: '5', name: 'Stilt Walker', pricePerHour: 200, description: 'Costumed stilt performers' },
    { id: '6', name: 'Capoeira Performer', pricePerHour: 150, description: 'Capoeira martial arts display' },
    { id: '7', name: 'Flag Bearer', pricePerHour: 100, description: 'Porta bandeira with school flag' },
    { id: '8', name: 'Mestre/Director', pricePerHour: 250, description: 'Mestre Curtis Pierre directing' },
  ],
  
  // Service packages
  packages: [
    { id: 'intimate', name: 'Intimate (1-5 performers)', basePrice: 500, description: 'Perfect for small gatherings' },
    { id: 'small', name: 'Small Group (6-12 performers)', basePrice: 1200, description: 'Great for parties and corporate events' },
    { id: 'medium', name: 'Medium Group (13-20 performers)', basePrice: 2500, description: 'Full sound and visual impact' },
    { id: 'large', name: 'Large Group (21-35 performers)', basePrice: 4500, description: 'Major events and festivals' },
    { id: 'production', name: 'Full Production (35+ performers)', basePrice: 7500, description: 'Complete carnival experience' },
  ],
  
  // Duration multipliers
  durationMultipliers: [
    { id: '15-30', name: '15-30 minutes', multiplier: 0.5 },
    { id: '30-45', name: '30-45 minutes', multiplier: 0.75 },
    { id: '45-60', name: '45-60 minutes', multiplier: 1.0 },
    { id: '1-2hrs', name: '1-2 hours', multiplier: 1.5 },
    { id: '2+hrs', name: '2+ hours', multiplier: 2.0 },
    { id: 'fullday', name: 'Full day event', multiplier: 3.5 },
  ],
  
  // Travel fees by distance
  travelFees: [
    { id: 'local', name: 'New Orleans Metro (0-30 miles)', fee: 0 },
    { id: 'regional', name: 'Regional (31-100 miles)', fee: 250 },
    { id: 'state', name: 'Louisiana (101-200 miles)', fee: 500 },
    { id: 'neighboring', name: 'Neighboring States (201-400 miles)', fee: 1000 },
    { id: 'national', name: 'National (400+ miles)', fee: 'Quote Required' },
  ],
  
  // Additional services/add-ons
  addOns: [
    { id: 'costumes', name: 'Premium Costumes', price: 500, description: 'Upgraded carnival costumes' },
    { id: 'sound', name: 'Sound System', price: 350, description: 'Professional PA system' },
    { id: 'lighting', name: 'LED Lighting', price: 250, description: 'Colorful LED effects' },
    { id: 'workshop', name: 'Dance Workshop (1hr)', price: 400, description: 'Interactive dance lesson' },
    { id: 'photo', name: 'Photo Op Station', price: 200, description: 'Backdrop and props for photos' },
  ],
  
  // Settings
  settings: {
    showPricesPublicly: true,
    minimumBookingFee: 500,
    depositPercentage: 50,
    taxRate: 0,
    currency: 'USD',
    disclaimerText: 'Prices are estimates. Final quote provided after consultation.',
  }
};

const PersonnelEditor = ({ personnel, onUpdate }) => {
  const [newItem, setNewItem] = useState({ name: '', pricePerHour: '', description: '' });

  const addItem = () => {
    if (newItem.name && newItem.pricePerHour) {
      onUpdate([...personnel, { ...newItem, id: Date.now().toString(), pricePerHour: parseFloat(newItem.pricePerHour) }]);
      setNewItem({ name: '', pricePerHour: '', description: '' });
    }
  };

  const updateItem = (id, field, value) => {
    onUpdate(personnel.map(p => p.id === id ? { ...p, [field]: field === 'pricePerHour' ? parseFloat(value) || 0 : value } : p));
  };

  const removeItem = (id) => {
    onUpdate(personnel.filter(p => p.id !== id));
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Users className="w-5 h-5 text-primary" />
        Personnel Pricing (per hour)
      </h3>
      
      <div className="space-y-3 mb-4">
        {personnel.map((person) => (
          <div key={person.id} className="flex items-center gap-3 group">
            <input
              type="text"
              value={person.name}
              onChange={(e) => updateItem(person.id, 'name', e.target.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
              placeholder="Role name"
            />
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">$</span>
              <input
                type="number"
                value={person.pricePerHour}
                onChange={(e) => updateItem(person.id, 'pricePerHour', e.target.value)}
                className="w-20 px-2 py-2 bg-background border border-border rounded-lg text-sm text-right"
              />
              <span className="text-muted-foreground text-sm">/hr</span>
            </div>
            <input
              type="text"
              value={person.description}
              onChange={(e) => updateItem(person.id, 'description', e.target.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
              placeholder="Description"
            />
            <button
              onClick={() => removeItem(person.id)}
              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg opacity-0 group-hover:opacity-100"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-4 border-t border-border">
        <input
          type="text"
          value={newItem.name}
          onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
          className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
          placeholder="New role name"
        />
        <input
          type="number"
          value={newItem.pricePerHour}
          onChange={(e) => setNewItem({ ...newItem, pricePerHour: e.target.value })}
          className="w-24 px-3 py-2 bg-background border border-border rounded-lg text-sm"
          placeholder="$/hr"
        />
        <button
          onClick={addItem}
          disabled={!newItem.name || !newItem.pricePerHour}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const PackagesEditor = ({ packages, onUpdate }) => {
  const updatePackage = (id, field, value) => {
    onUpdate(packages.map(p => p.id === id ? { ...p, [field]: field === 'basePrice' ? parseFloat(value) || 0 : value } : p));
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-purple-400" />
        Package Base Pricing
      </h3>
      
      <div className="space-y-3">
        {packages.map((pkg) => (
          <div key={pkg.id} className="flex items-center gap-3">
            <input
              type="text"
              value={pkg.name}
              onChange={(e) => updatePackage(pkg.id, 'name', e.target.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
            />
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">$</span>
              <input
                type="number"
                value={pkg.basePrice}
                onChange={(e) => updatePackage(pkg.id, 'basePrice', e.target.value)}
                className="w-24 px-2 py-2 bg-background border border-border rounded-lg text-sm text-right"
              />
            </div>
            <input
              type="text"
              value={pkg.description}
              onChange={(e) => updatePackage(pkg.id, 'description', e.target.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
              placeholder="Description"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const DurationEditor = ({ durations, onUpdate }) => {
  const updateDuration = (id, field, value) => {
    onUpdate(durations.map(d => d.id === id ? { ...d, [field]: field === 'multiplier' ? parseFloat(value) || 0 : value } : d));
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-green-400" />
        Duration Multipliers
      </h3>
      <p className="text-sm text-muted-foreground mb-4">Base price is multiplied by these factors</p>
      
      <div className="space-y-3">
        {durations.map((dur) => (
          <div key={dur.id} className="flex items-center gap-3">
            <input
              type="text"
              value={dur.name}
              onChange={(e) => updateDuration(dur.id, 'name', e.target.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="0.1"
                value={dur.multiplier}
                onChange={(e) => updateDuration(dur.id, 'multiplier', e.target.value)}
                className="w-20 px-2 py-2 bg-background border border-border rounded-lg text-sm text-right"
              />
              <span className="text-muted-foreground text-sm">x</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TravelFeesEditor = ({ travelFees, onUpdate }) => {
  const updateFee = (id, field, value) => {
    onUpdate(travelFees.map(t => t.id === id ? { ...t, [field]: field === 'fee' && value !== 'Quote Required' ? parseFloat(value) || 0 : value } : t));
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <MapPin className="w-5 h-5 text-blue-400" />
        Travel Fees
      </h3>
      
      <div className="space-y-3">
        {travelFees.map((travel) => (
          <div key={travel.id} className="flex items-center gap-3">
            <input
              type="text"
              value={travel.name}
              onChange={(e) => updateFee(travel.id, 'name', e.target.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
            />
            <div className="flex items-center gap-1">
              {travel.fee === 'Quote Required' ? (
                <input
                  type="text"
                  value={travel.fee}
                  onChange={(e) => updateFee(travel.id, 'fee', e.target.value)}
                  className="w-32 px-2 py-2 bg-background border border-border rounded-lg text-sm text-right"
                />
              ) : (
                <>
                  <span className="text-muted-foreground">$</span>
                  <input
                    type="number"
                    value={travel.fee}
                    onChange={(e) => updateFee(travel.id, 'fee', e.target.value)}
                    className="w-24 px-2 py-2 bg-background border border-border rounded-lg text-sm text-right"
                  />
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AddOnsEditor = ({ addOns, onUpdate }) => {
  const [newItem, setNewItem] = useState({ name: '', price: '', description: '' });

  const addItem = () => {
    if (newItem.name && newItem.price) {
      onUpdate([...addOns, { ...newItem, id: Date.now().toString(), price: parseFloat(newItem.price) }]);
      setNewItem({ name: '', price: '', description: '' });
    }
  };

  const updateItem = (id, field, value) => {
    onUpdate(addOns.map(a => a.id === id ? { ...a, [field]: field === 'price' ? parseFloat(value) || 0 : value } : a));
  };

  const removeItem = (id) => {
    onUpdate(addOns.filter(a => a.id !== id));
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Plus className="w-5 h-5 text-orange-400" />
        Add-On Services
      </h3>
      
      <div className="space-y-3 mb-4">
        {addOns.map((addon) => (
          <div key={addon.id} className="flex items-center gap-3 group">
            <input
              type="text"
              value={addon.name}
              onChange={(e) => updateItem(addon.id, 'name', e.target.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
            />
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">$</span>
              <input
                type="number"
                value={addon.price}
                onChange={(e) => updateItem(addon.id, 'price', e.target.value)}
                className="w-24 px-2 py-2 bg-background border border-border rounded-lg text-sm text-right"
              />
            </div>
            <input
              type="text"
              value={addon.description}
              onChange={(e) => updateItem(addon.id, 'description', e.target.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
              placeholder="Description"
            />
            <button
              onClick={() => removeItem(addon.id)}
              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg opacity-0 group-hover:opacity-100"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-4 border-t border-border">
        <input
          type="text"
          value={newItem.name}
          onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
          className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm"
          placeholder="Add-on name"
        />
        <input
          type="number"
          value={newItem.price}
          onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
          className="w-24 px-3 py-2 bg-background border border-border rounded-lg text-sm"
          placeholder="Price"
        />
        <button
          onClick={addItem}
          disabled={!newItem.name || !newItem.price}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const SettingsEditor = ({ settings, onUpdate }) => {
  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-yellow-400" />
        Pricing Settings
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Minimum Booking Fee</label>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">$</span>
            <input
              type="number"
              value={settings.minimumBookingFee}
              onChange={(e) => onUpdate({ ...settings, minimumBookingFee: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Required Deposit (%)</label>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={settings.depositPercentage}
              onChange={(e) => onUpdate({ ...settings, depositPercentage: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg"
            />
            <span className="text-muted-foreground">%</span>
          </div>
        </div>
        
        <div className="md:col-span-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showPricesPublicly}
              onChange={(e) => onUpdate({ ...settings, showPricesPublicly: e.target.checked })}
              className="w-5 h-5 rounded border-border"
            />
            <span className="font-medium">Show prices publicly on booking page</span>
          </label>
          <p className="text-sm text-muted-foreground mt-1 ml-8">
            If unchecked, customers will see "Contact for Quote" instead of prices
          </p>
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2">Disclaimer Text</label>
          <textarea
            value={settings.disclaimerText}
            onChange={(e) => onUpdate({ ...settings, disclaimerText: e.target.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg resize-none"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
};

export const AdminPricingEditor = () => {
  const [config, setConfig] = useState(defaultPricingConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await adminFetch('/pricing-config');
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setConfig({ ...defaultPricingConfig, ...data });
        }
      }
    } catch (error) {
      console.error('Error fetching pricing config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await adminFetch('/admin/pricing-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        toast.success('Pricing configuration saved!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save pricing configuration');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('Reset all pricing to defaults? This cannot be undone.')) {
      setConfig(defaultPricingConfig);
      toast.success('Reset to default pricing');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-white/5 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Pricing Editor</h1>
              <p className="text-muted-foreground">Configure personnel rates and service packages</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/booking#pricing"
              target="_blank"
              className="flex items-center gap-2 px-4 py-2 bg-stone-700 hover:bg-stone-600 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4" />
              Preview
            </Link>
            <button
              onClick={resetToDefaults}
              className="flex items-center gap-2 px-4 py-2 bg-stone-700 hover:bg-stone-600 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={saveConfig}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg font-bold hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-8">
          <p className="text-sm text-foreground">
            <strong>Pricing Guide:</strong> Set your rates for different personnel, packages, duration multipliers, travel fees, and add-on services. 
            These will be displayed on the booking page to help customers estimate costs.
          </p>
        </div>

        {/* Editor Sections */}
        <div className="space-y-6">
          <SettingsEditor 
            settings={config.settings} 
            onUpdate={(settings) => setConfig({ ...config, settings })} 
          />
          
          <PersonnelEditor 
            personnel={config.personnel} 
            onUpdate={(personnel) => setConfig({ ...config, personnel })} 
          />
          
          <PackagesEditor 
            packages={config.packages} 
            onUpdate={(packages) => setConfig({ ...config, packages })} 
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DurationEditor 
              durations={config.durationMultipliers} 
              onUpdate={(durationMultipliers) => setConfig({ ...config, durationMultipliers })} 
            />
            
            <TravelFeesEditor 
              travelFees={config.travelFees} 
              onUpdate={(travelFees) => setConfig({ ...config, travelFees })} 
            />
          </div>
          
          <AddOnsEditor 
            addOns={config.addOns} 
            onUpdate={(addOns) => setConfig({ ...config, addOns })} 
          />
        </div>

        {/* Save Button (Bottom) */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-xl font-bold text-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Save All Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPricingEditor;
